import { useMemo } from 'react';

import { useApplicationContext } from '@/context/v1/Application/context';
import { useKimLhc } from '@/context/KimLhc';

import { useStepMutation } from '@/components/views/Report/KimLhc/hooks/useStepMutation';
import { useUpdateStepAndCommentKimLhcReport } from '@/hooks/useUpdateStepAndCommentKimLhcReport';

import { FormFields, FieldNamesEnum } from '@/components/views/Report/KimLhc/types';

interface PostureParams {
	id: string;
	file_id: string;
	company_id: string;
	organization_id: string;
	body_posture?: string;
	posture_additional_factor_twist?: string | null;
	posture_additional_factor_load_center?: string | null;
	posture_additional_factor_arms?: string | null;
	posture_additional_factor_hands?: string | null;
}

const POSTURE_FIELD_PATHS: [string, string][] = [
	[FieldNamesEnum.POSTURE, 'body_posture'],
	[FieldNamesEnum.POSTURE, 'posture_additional_factor_twist'],
	[FieldNamesEnum.POSTURE, 'posture_additional_factor_load_center'],
	[FieldNamesEnum.POSTURE, 'posture_additional_factor_arms'],
	[FieldNamesEnum.POSTURE, 'posture_additional_factor_hands']
];

export const usePosture = () => {
	const { file_id, report_id, form, onNextStep, currentStep } = useKimLhc();

	const { organization, company } = useApplicationContext();
	const { mutate, isLoading: isLoadingStepMutation } = useStepMutation<PostureParams>();
	const { mutateAsync: updateStepAndCommentKimLhcReport, isLoading: isLoadingUpdateStepAndComment } =
		useUpdateStepAndCommentKimLhcReport();
	const previousValues = useMemo(() => {
		return {
			body_posture: form.getFieldValue([FieldNamesEnum.POSTURE, 'body_posture']),
			posture_additional_factor_twist: form.getFieldValue([FieldNamesEnum.POSTURE, 'posture_additional_factor_twist']),
			posture_additional_factor_load_center: form.getFieldValue([FieldNamesEnum.POSTURE, 'posture_additional_factor_load_center']),
			posture_additional_factor_arms: form.getFieldValue([FieldNamesEnum.POSTURE, 'posture_additional_factor_arms']),
			posture_additional_factor_hands: form.getFieldValue([FieldNamesEnum.POSTURE, 'posture_additional_factor_hands'])
		};
	}, [form]);

	const buildUpdatePayload = (values: FormFields): PostureParams | null => {
		if (!report_id || !file_id) {
			return null;
		}

		const postureData = values.posture;

		return {
			id: report_id,
			file_id: file_id,
			company_id: company.id,
			organization_id: organization.id,
			body_posture: postureData.body_posture,
			posture_additional_factor_twist: postureData.posture_additional_factor_twist ?? null,
			posture_additional_factor_load_center: postureData.posture_additional_factor_load_center ?? null,
			posture_additional_factor_arms: postureData.posture_additional_factor_arms ?? null,
			posture_additional_factor_hands: postureData.posture_additional_factor_hands ?? null
		};
	};

	function getCurrentValues() {
		return {
			body_posture: form.getFieldValue([FieldNamesEnum.POSTURE, 'body_posture']),
			posture_additional_factor_twist: form.getFieldValue([FieldNamesEnum.POSTURE, 'posture_additional_factor_twist']),
			posture_additional_factor_load_center: form.getFieldValue([FieldNamesEnum.POSTURE, 'posture_additional_factor_load_center']),
			posture_additional_factor_arms: form.getFieldValue([FieldNamesEnum.POSTURE, 'posture_additional_factor_arms']),
			posture_additional_factor_hands: form.getFieldValue([FieldNamesEnum.POSTURE, 'posture_additional_factor_hands'])
		};
	}

	function hasChanges(): boolean {
		const currentValues = getCurrentValues();
		return POSTURE_FIELD_PATHS.some(path => currentValues[path[1] as keyof typeof currentValues] !== previousValues[path[1] as keyof typeof previousValues]);
	}

	const handleSubmit = async (values: FormFields): Promise<void> => {
		if (!report_id || !hasChanges()) {
			await updateStepAndCommentKimLhcReport({
				id: report_id!,
				file_id: file_id!,
				step: currentStep + 1,
				organization_id: organization.id,
				company_id: company.id
			});
			onNextStep();
			return;
		}

		const payload = buildUpdatePayload(values);
		if (!payload) {
			return;
		}

		await mutate(payload);
	};

	const isLoading = isLoadingStepMutation || isLoadingUpdateStepAndComment;

	return {
		handleSubmit,
		isLoading
	};
};
